import {
  type ReactElement,
  type ReactNode,
  type KeyboardEvent,
  type RefObject,
  type CSSProperties,
  useEffect,
  useRef,
  useState,
} from "react";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  type CollisionDetection,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";
import type {
  SipocParams,
  SipocRow,
  SipocPartyEntry,
  SipocPartyClassification,
} from "@playbookai/widget-schemas";
import { widgetTargetKey } from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import { Button } from "../../atoms/Button/Button.js";
import { useFieldEditSession } from "../field-edit-session.js";
import type { WidgetBodyProps } from "../types.js";
import {
  SipocEntryDetails,
  type SipocEntryDetailValues,
} from "./SipocEntryDetails.js";
import { SIPOC_COLUMN_HINTS, SIPOC_COLUMN_TITLES } from "./SipocGuide.js";

/**
 * Grow a one-row textarea with its text.
 *
 * Where the browser supports `field-sizing: content` the `field-sizing-content`
 * class on the element does it in layout — deterministic, fonts included — and
 * this hook does nothing. Elsewhere it falls back to measuring `scrollHeight`,
 * and re-measures once the web fonts are in: a measurement taken before they
 * load uses fallback metrics and was never corrected, which is how two renders
 * of the same story came out at different heights in visual regression.
 */
const SUPPORTS_FIELD_SIZING =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("field-sizing", "content");

function useAutoGrow(
  ref: RefObject<HTMLTextAreaElement | null>,
  text: string,
): void {
  useEffect(() => {
    if (SUPPORTS_FIELD_SIZING) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const measure = (): void => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    measure();
    let cancelled = false;
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(() => {
        if (!cancelled) measure();
      });
    }
    // A narrower column wraps more lines under the same text; with
    // `overflow-hidden` those lines would be clipped unless re-measured.
    // Width only — the measurement itself changes the height.
    let lastWidth = el.getBoundingClientRect().width;
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? lastWidth;
            if (width === lastWidth) return;
            lastWidth = width;
            measure();
          });
    observer?.observe(el);
    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [ref, text]);
}

export type SipocLinkState =
  | "not-added"
  | "added"
  | "different"
  | "map-item-removed";

/**
 * `md` is the panel scale the expanded editor mounts at. `lg` is the canvas-card
 * scale: the artifact card is 2000×920 flow units and is read at 45–80% canvas
 * zoom, so every type step goes up the ramp and the cells get more air — the
 * same board, still editable in place, just legible without expanding it.
 */
export type SipocSize = "md" | "lg";

export interface SipocWidgetProps extends WidgetBodyProps<SipocParams> {
  /**
   * Opt-in map line under each step. Absent, no line is drawn; present, a row
   * missing from the map reads "Not on Current State map".
   */
  readonly linkStateByRowId?: Readonly<Record<string, SipocLinkState>>;
  readonly size?: SipocSize;
  /** Add narrow-editor section headings; compact canvas cards keep their grid. */
  readonly expandedLayout?: boolean;
  /**
   * A host-owned band rendered above the board (the VSM host's "Add N SIPOC
   * flows to Current State" offer). The board only places it; it stays
   * data-blind about what the band says or does.
   */
  readonly offerSlot?: ReactNode;
}

/**
 * SIPOC — the VSM scoping board (VSM-WS9). A 5-column grid — Suppliers · Inputs
 * · Process · Outputs · Customers — where each row is one Process step plus four
 * multi-entry lists. Data-blind and inline-editable: it renders `data` and emits
 * every add/remove/edit/reorder through `onChange`; `readOnly` hides the edit
 * affordances. A host-supplied link-state map, when given, renders the
 * current-map synchronization state without exposing link identities.
 *
 * One board, two scales: the expanded editor (`SipocExpandedEditor`) mounts it at
 * `md`, the canvas card (`SipocCard`) at `lg`. Both edit in place. It frames the
 * grid with five colour-keyed column headers (the guide and the per-column
 * hints live in the host's `SipocGuideButton`), numbers each process step, and
 * lets whole steps be reordered by dragging the grip (or ArrowUp/ArrowDown on
 * it) — mirroring the per-entry reorder the list cells already have. Past the
 * height it is given, the board scrolls inside its own container.
 */

type ListColumn = "suppliers" | "inputs" | "outputs" | "customers";

const COLUMN_TITLES: Record<ListColumn | "process", string> =
  SIPOC_COLUMN_TITLES;

const ADD_LABELS: Record<ListColumn, string> = {
  suppliers: "Add supplier",
  inputs: "Add input",
  outputs: "Add output",
  customers: "Add customer",
};

// Column render order — Process is the pivot in the middle.
const COLUMN_ORDER = [
  "suppliers",
  "inputs",
  "process",
  "outputs",
  "customers",
] as const;

// Colour-key dot per column header.
const COLUMN_DOT: Record<(typeof COLUMN_ORDER)[number], string> = {
  suppliers: "bg-pb-cyan",
  inputs: "bg-pb-cyan/60",
  process: "bg-pb-tint-7",
  outputs: "bg-pb-green/60",
  customers: "bg-pb-green",
};

// The compact canvas card keeps its established proportions. In the expanded
// editor, give the four detail columns more room than the process spine.
const GRID = "grid grid-cols-[1fr_1fr_1.2fr_1fr_1fr]";
const EXPANDED_GRID = "grid grid-cols-[1.2fr_1.25fr_1fr_1.25fr_1.2fr]";
// The board is wider than a shrunk panel, so it scrolls inside its own overflow
// container (never overflowing the page) — the same pattern the design uses.
const BOARD_MIN_WIDTH = "min-w-[52rem]";
const EXPANDED_BOARD_MIN_WIDTH = "min-w-[68rem]";

const sipocCollisionDetection: CollisionDetection = (args) =>
  pointerWithin({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (container) =>
        container.data.current?.kind === args.active.data.current?.kind &&
        (args.active.data.current?.kind !== "entry" ||
          (container.data.current?.rowId === args.active.data.current?.rowId &&
            container.data.current?.column ===
              args.active.data.current?.column)),
    ),
  });

type SortableHandle = Pick<
  ReturnType<typeof useSortable>,
  "setActivatorNodeRef" | "attributes" | "listeners"
>;

/**
 * Every size-dependent class, keyed by scale. `lg` steps each type size up the
 * DS ramp (dense-2 → sm, xs → lg, sm → xl, …) and widens the paddings, so the
 * board reads at canvas zoom; nothing structural changes between the two.
 */
const SCALE = {
  md: {
    header: "gap-1.5 px-3.5 py-2 text-dense-2",
    headerDot: "h-1.5 w-1.5",
    cell: "gap-1.5 px-3 py-3",
    entry: "min-h-7 gap-1.5 px-2.5",
    entryText: "py-1 text-caption leading-4",
    entryReadOnly: "px-2.5 py-2 text-xs",
    entryMeta: "mt-0.5 text-dense-3",
    classify: "px-1.5 py-px text-dense-3",
    entryIcon: "h-3 w-3",
    entryControl: "h-7",
    smallButton: "h-7 px-1.5",
    processCell: "gap-1.5 px-3 py-3",
    processBox: "gap-2 px-3 py-2.5",
    // Tucked into the box's start padding, as the design draws the handle.
    grip: "-ms-1.5",
    gripIcon: "h-3.5 w-3.5",
    badge: "h-6 w-6 text-caption",
    processText: "text-sm leading-5",
    boundary: "-top-2 end-3 px-1.5 py-px text-dense-3",
    link: "gap-1 text-dense-3",
    linkDot: "h-1 w-1",
    removeStep: "px-1.5 py-0.5 text-dense-2",
    empty: "px-4 py-10 text-sm",
    addStep: "p-3",
    addStepIcon: "h-3.5 w-3.5",
  },
  lg: {
    header: "gap-2 px-4 py-3 text-base",
    headerDot: "h-2 w-2",
    cell: "gap-2.5 px-4 py-4",
    entry: "min-h-12 gap-1.5 px-3",
    entryText: "py-2.5 text-lg leading-6",
    entryReadOnly: "px-3.5 py-3 text-lg",
    entryMeta: "mt-1 text-sm",
    classify: "px-2 py-0.5 text-xs",
    entryIcon: "h-4 w-4",
    entryControl: "h-9",
    smallButton: "h-9 px-2 text-base",
    processCell: "gap-2.5 px-4 py-4",
    processBox: "gap-3 px-4 py-3.5",
    grip: "-ms-2",
    gripIcon: "h-5 w-5",
    badge: "h-8 w-8 text-base",
    processText: "text-xl leading-7",
    boundary: "-top-2.5 end-4 px-2 py-0.5 text-xs",
    link: "gap-1.5 text-sm",
    linkDot: "h-1.5 w-1.5",
    removeStep: "px-2 py-1 text-sm",
    empty: "px-5 py-14 text-lg",
    addStep: "p-4",
    addStepIcon: "h-4 w-4",
  },
} as const satisfies Record<SipocSize, Record<string, string>>;

type SipocScale = (typeof SCALE)[SipocSize];

function makeId(prefix: string): string {
  const rnd =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${prefix}-${rnd}`;
}

/** Matches SipocParamsSchema's `z.array(...).max(50)` on rows and each column. */
const MAX_SIPOC_ROWS = 50;
const MAX_SIPOC_ENTRIES = 50;
/** Process + entry text are `MediumText` (≤500) in SipocParamsSchema. */
const SIPOC_TEXT_MAX = 500;

/**
 * An entry nobody has put anything in. `text` is not the whole entry: a party
 * entry may carry a `classification`, a flow entry `requirements` or `notes`,
 * and either may already be linked to a map shape. Reading only `text` would
 * refuse a legitimate add — classify a supplier, then ask for the next one and
 * nothing happens.
 */
function isBlankEntry(entry: SipocRow[ListColumn][number]): boolean {
  // `SipocFlowEntry` is not exported, so read the two flow-only fields
  // structurally rather than widening the schema package's surface for a guard.
  const detail = entry as {
    readonly classification?: SipocPartyClassification | undefined;
    readonly requirements?: string | undefined;
    readonly notes?: string | undefined;
  };
  return (
    entry.text.trim() === "" &&
    entry.linkedEntityId === undefined &&
    detail.classification === undefined &&
    (detail.requirements ?? "").trim() === "" &&
    (detail.notes ?? "").trim() === ""
  );
}

/**
 * A step nobody has typed into yet — no name, and nothing in any of the four
 * party columns. Deliberately whole-row rather than `process` alone: filling
 * Suppliers before naming the step is a legitimate order of work, and treating
 * that as blank would refuse the next step to someone mid-flow.
 *
 * A column of EMPTY entry shells is still blank. Asking for a supplier and not
 * typing one leaves a row that reads as untouched, and counting that shell as
 * content let the second "Add process step" through — the very stacking this
 * guard exists to stop.
 */
function isBlankRow(row: SipocRow): boolean {
  return (
    row.process.trim() === "" &&
    row.suppliers.every(isBlankEntry) &&
    row.inputs.every(isBlankEntry) &&
    row.outputs.every(isBlankEntry) &&
    row.customers.every(isBlankEntry)
  );
}

function emptyRow(): SipocRow {
  return {
    id: makeId("row"),
    process: "",
    suppliers: [],
    inputs: [],
    outputs: [],
    customers: [],
  };
}

export function SipocWidget({
  data,
  onChange,
  readOnly,
  linkStateByRowId,
  size = "md",
  expandedLayout = false,
  offerSlot,
}: SipocWidgetProps): ReactElement {
  const { rows } = data;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const s = SCALE[size];
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  // The row under a dragged step, so the drop target reads as the design's
  // accent rail rather than nothing happening until release.
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
  // Focus follows an add: the new step or entry is where the next keystroke
  // belongs, so nobody has to click into the field they just made. Resolved
  // by id after the render that mounts it (`data-sipoc-focus-id`), then
  // cleared, so a later re-render never steals focus back.
  const rootRef = useRef<HTMLDivElement>(null);
  const [pendingFocusId, setPendingFocusId] = useState<string | null>(null);
  useEffect(() => {
    if (pendingFocusId === null) return;
    const target = rootRef.current?.querySelector<HTMLElement>(
      `[data-sipoc-focus-id="${pendingFocusId}"]`,
    );
    if (!target) return;
    target.focus();
    setPendingFocusId(null);
  }, [pendingFocusId, rows]);
  const setRows = (next: SipocRow[]): void => {
    onChange({ ...data, rows: next });
  };

  const updateProcess = (rowId: string, process: string): void =>
    setRows(rows.map((r) => (r.id === rowId ? { ...r, process } : r)));
  // SipocParamsSchema caps rows and each column's entries at 50; guard at the
  // point of entry so an add can never emit params that fail validation.
  //
  // The cap was the ONLY guard, so every click appended another blank step and a
  // facilitator clicking twice while thinking got two empty rows to clean up
  // (PB-5229's sibling, PB-5186). Rather than disable the button — which makes
  // the click do nothing and says nothing about why — an add onto an untouched
  // trailing step just puts the caret back in it. That is this widget's existing
  // "focus follows an add" contract, reused: the blank step already IS where the
  // next keystroke belongs.
  //
  // Blankness is whole-row: a step whose Suppliers are filled but whose name is
  // not yet typed is content, and appending after it is what the user meant.
  const addRow = (): void => {
    if (rows.length >= MAX_SIPOC_ROWS) return;
    const trailing = rows[rows.length - 1];
    if (trailing && isBlankRow(trailing)) {
      setPendingFocusId(trailing.id);
      return;
    }
    const row = emptyRow();
    setRows([...rows, row]);
    setPendingFocusId(row.id);
  };
  const removeRow = (rowId: string): void =>
    setRows(rows.filter((r) => r.id !== rowId));
  // Whole-step reorder — mirrors `moveEntry`'s splice, keyed by row id so nothing
  // that reads a row (link state, entries) shifts. No-op guards keep the payload
  // schema-valid and identical when the move would change nothing.
  const moveRow = (rowId: string, nextIndex: number): void => {
    const currentIndex = rows.findIndex((r) => r.id === rowId);
    if (
      currentIndex < 0 ||
      nextIndex < 0 ||
      nextIndex >= rows.length ||
      currentIndex === nextIndex
    ) {
      return;
    }
    const next = [...rows];
    const [moved] = next.splice(currentIndex, 1);
    if (!moved) return;
    next.splice(nextIndex, 0, moved);
    setRows(next);
  };

  const patchRowCol = (
    rowId: string,
    column: ListColumn,
    fn: (list: SipocPartyEntry[]) => SipocPartyEntry[],
  ): void =>
    setRows(
      rows.map((r) => (r.id === rowId ? { ...r, [column]: fn(r[column]) } : r)),
    );
  // Same defect, same remedy, one column down: this is the other add button on
  // the widget, and QA will click it (PB-5186). Blankness here is the whole
  // entry, not its `text` — see `isBlankEntry`.
  const addEntry = (rowId: string, column: ListColumn): void => {
    const row = rows.find((candidate) => candidate.id === rowId);
    if (!row || row[column].length >= MAX_SIPOC_ENTRIES) return;
    const trailing = row[column][row[column].length - 1];
    if (trailing && isBlankEntry(trailing)) {
      setPendingFocusId(trailing.id);
      return;
    }
    const entry: SipocPartyEntry = { id: makeId("ent"), text: "" };
    patchRowCol(rowId, column, (list) => [...list, entry]);
    setPendingFocusId(entry.id);
  };
  const updateEntry = (
    rowId: string,
    column: ListColumn,
    entryId: string,
    text: string,
  ): void =>
    patchRowCol(rowId, column, (list) =>
      list.map((e) => (e.id === entryId ? { ...e, text } : e)),
    );
  const updateClassification = (
    rowId: string,
    column: "suppliers" | "customers",
    entryId: string,
    classification: SipocPartyClassification | undefined,
  ): void =>
    patchRowCol(rowId, column, (list) =>
      list.map((entry) =>
        entry.id === entryId ? { ...entry, classification } : entry,
      ),
    );
  const moveEntry = (
    rowId: string,
    column: ListColumn,
    entryId: string,
    nextIndex: number,
  ): void =>
    patchRowCol(rowId, column, (list) => {
      const currentIndex = list.findIndex((entry) => entry.id === entryId);
      if (
        currentIndex < 0 ||
        nextIndex < 0 ||
        nextIndex >= list.length ||
        currentIndex === nextIndex
      ) {
        return list;
      }
      const next = [...list];
      const [entry] = next.splice(currentIndex, 1);
      if (!entry) return list;
      next.splice(nextIndex, 0, entry);
      return next;
    });
  const removeEntry = (
    rowId: string,
    column: ListColumn,
    entryId: string,
  ): void =>
    patchRowCol(rowId, column, (list) => list.filter((e) => e.id !== entryId));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={sipocCollisionDetection}
      onDragStart={({ active }) =>
        setDraggedRowId(
          active.data.current?.kind === "row" ? String(active.id) : null,
        )
      }
      onDragOver={({ active, over }) =>
        setDragOverRowId(
          active.data.current?.kind === "row" && over ? String(over.id) : null,
        )
      }
      onDragCancel={() => {
        setDraggedRowId(null);
        setDragOverRowId(null);
      }}
      onDragEnd={({ active, over }) => {
        if (!readOnly && over) {
          const source = active.data.current;
          const target = over.data.current;
          if (source?.kind === "row" && target?.kind === "row") {
            moveRow(
              String(active.id),
              rows.findIndex((row) => row.id === over.id),
            );
          } else if (
            source?.kind === "entry" &&
            target?.kind === "entry" &&
            source.rowId === target.rowId &&
            source.column === target.column
          ) {
            const row = rows.find((candidate) => candidate.id === source.rowId);
            const column = source.column as ListColumn;
            if (row) {
              moveEntry(
                row.id,
                column,
                String(active.id),
                row[column].findIndex((entry) => entry.id === over.id),
              );
            }
          }
        }
        setDraggedRowId(null);
        setDragOverRowId(null);
      }}
    >
      <div
        ref={rootRef}
        data-size={size}
        className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-auto"
      >
        <div
          className={cn(
            expandedLayout ? EXPANDED_BOARD_MIN_WIDTH : BOARD_MIN_WIDTH,
            "flex-1 @max-[68rem]/sipoc-editor:min-w-0",
          )}
        >
          {offerSlot ? (
            <div
              data-testid="sipoc-offer-slot"
              className={cn("border-b border-pb-border", s.addStep)}
            >
              {offerSlot}
            </div>
          ) : null}
          {/* The column headers stay pinned while the steps scroll, like a
              frozen spreadsheet header (UX-12). The wrapper sits INSIDE the
              min-width board, so a sideways scroll keeps each header over its
              column, and its solid fill stops rows showing through. The card
              and the expanded editor scroll in this same root, so both scales
              get it. */}
          <div className="sticky top-0 z-10 bg-pb-card">
            {/* Column headers with colour keys. */}
            <div
              className={cn(
                expandedLayout ? EXPANDED_GRID : GRID,
                "border-b border-pb-border bg-pb-card-elevated shadow-sm @max-[68rem]/sipoc-editor:hidden",
              )}
            >
              {COLUMN_ORDER.map((col, i) => (
                <div
                  key={col}
                  className={cn(
                    "relative flex min-w-0 items-center font-semibold uppercase tracking-wider text-pb-text-secondary",
                    s.header,
                    i > 0 && "border-s border-pb-border",
                    col === "process" && "justify-center text-pb-iris-text",
                  )}
                >
                  <span
                    className={cn(
                      "shrink-0 rounded-full",
                      s.headerDot,
                      COLUMN_DOT[col],
                    )}
                    aria-hidden="true"
                  />
                  {/* The bold initials spell S·I·P·O·C across the row. A
                      first-letter style, not a split span, so each title stays
                      one word to a screen reader and to a text query. */}
                  <span className="truncate first-letter:font-bold first-letter:text-pb-text">
                    {COLUMN_TITLES[col]}
                  </span>
                  {/* What goes in the column, for screen readers at BOTH
                      scales: a header is a plain cell with no role, so a
                      description reference would announce nothing. Sighted
                      readers get the same lines from the host's guide button
                      (`SipocGuideButton`); the words live in one place. */}
                  <span className="sr-only">{SIPOC_COLUMN_HINTS[col]}</span>
                </div>
              ))}
            </div>
          </div>

          <SortableContext
            items={rows.map((row) => row.id)}
            strategy={verticalListSortingStrategy}
          >
            {rows.map((row, index) => (
              <SipocRowView
                key={row.id}
                row={row}
                index={index}
                rowCount={rows.length}
                readOnly={readOnly}
                expandedLayout={expandedLayout}
                s={s}
                dragging={draggedRowId === row.id}
                dropTarget={
                  draggedRowId !== null &&
                  draggedRowId !== row.id &&
                  dragOverRowId === row.id
                }
                onProcessChange={(text) => updateProcess(row.id, text)}
                onAddRow={addRow}
                onRemoveRow={() => removeRow(row.id)}
                onMoveRow={(nextIndex) => moveRow(row.id, nextIndex)}
                onAddEntry={(column) => addEntry(row.id, column)}
                onUpdateEntry={(column, entryId, text) =>
                  updateEntry(row.id, column, entryId, text)
                }
                onUpdateDetails={(column, entryId, details) => {
                  if (readOnly) return;
                  patchRowCol(row.id, column, (list) =>
                    list.map((entry) =>
                      entry.id === entryId ? { ...entry, ...details } : entry,
                    ),
                  );
                }}
                onRemoveEntry={(column, entryId) =>
                  removeEntry(row.id, column, entryId)
                }
                onUpdateClassification={(column, entryId, classification) =>
                  updateClassification(row.id, column, entryId, classification)
                }
                onMoveEntry={(column, entryId, nextIndex) =>
                  moveEntry(row.id, column, entryId, nextIndex)
                }
                linkState={
                  linkStateByRowId
                    ? (linkStateByRowId[row.id] ?? "not-added")
                    : undefined
                }
              />
            ))}
          </SortableContext>

          {rows.length === 0 ? (
            <div className={cn("text-center text-pb-text-muted", s.empty)}>
              {readOnly
                ? "No rows yet."
                : "No process steps yet. Add a step to start defining the flow."}
            </div>
          ) : null}

          {readOnly ? null : (
            <div className={cn("border-t border-pb-border", s.addStep)}>
              {/* A dashed add row (look row OK-06). */}
              <Button
                variant="dashed"
                size={size === "lg" ? "md" : "sm"}
                onClick={addRow}
                disabled={rows.length >= MAX_SIPOC_ROWS}
                className={cn("w-full", size === "lg" && "text-base")}
              >
                <Plus className={s.addStepIcon} aria-hidden="true" />
                Add process step
              </Button>
            </div>
          )}
        </div>
      </div>
    </DndContext>
  );
}

function SipocRowView({
  row,
  index,
  rowCount,
  readOnly,
  expandedLayout,
  s,
  dragging,
  dropTarget,
  onProcessChange,
  onAddRow,
  onRemoveRow,
  onMoveRow,
  onAddEntry,
  onUpdateEntry,
  onUpdateDetails,
  onRemoveEntry,
  onUpdateClassification,
  onMoveEntry,
  linkState,
}: {
  row: SipocRow;
  index: number;
  rowCount: number;
  readOnly: boolean | undefined;
  expandedLayout: boolean;
  s: SipocScale;
  dragging: boolean;
  dropTarget: boolean;
  onProcessChange: (text: string) => void;
  onAddRow: () => void;
  onRemoveRow: () => void;
  onMoveRow: (nextIndex: number) => void;
  onAddEntry: (column: ListColumn) => void;
  onUpdateEntry: (column: ListColumn, entryId: string, text: string) => void;
  onUpdateDetails: (
    column: "inputs" | "outputs",
    entryId: string,
    details: SipocEntryDetailValues,
  ) => void;
  onRemoveEntry: (column: ListColumn, entryId: string) => void;
  onUpdateClassification: (
    column: "suppliers" | "customers",
    entryId: string,
    classification: SipocPartyClassification | undefined,
  ) => void;
  onMoveEntry: (column: ListColumn, entryId: string, nextIndex: number) => void;
  linkState: SipocLinkState | undefined;
}): ReactElement {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
  } = useSortable({
    id: row.id,
    disabled: readOnly === true,
    data: { kind: "row", rowId: row.id },
  });
  const listCell = (column: ListColumn, withDivider = true): ReactElement => (
    <SipocListCell
      column={column}
      entries={row[column]}
      rowId={row.id}
      readOnly={readOnly}
      expandedLayout={expandedLayout}
      s={s}
      withDivider={withDivider}
      onAdd={() => onAddEntry(column)}
      onUpdate={(entryId, text) => onUpdateEntry(column, entryId, text)}
      onUpdateDetails={(entryId, details) => {
        if (column === "inputs" || column === "outputs")
          onUpdateDetails(column, entryId, details);
      }}
      onRemove={(entryId) => onRemoveEntry(column, entryId)}
      onClassificationChange={(entryId, classification) => {
        if (column === "suppliers" || column === "customers") {
          onUpdateClassification(column, entryId, classification);
        }
      }}
      onMove={(entryId, nextIndex) => onMoveEntry(column, entryId, nextIndex)}
    />
  );
  return (
    // The process-cell grip is the drag SOURCE; the whole row is the drop TARGET.
    <div
      className={cn(
        expandedLayout ? EXPANDED_GRID : GRID,
        "group/row border-b border-pb-border bg-pb-card transition-colors motion-reduce:!transition-none last:border-b-0 hover:pb-overlay-paint @max-[68rem]/sipoc-editor:mx-3 @max-[68rem]/sipoc-editor:my-2 @max-[68rem]/sipoc-editor:overflow-hidden @max-[68rem]/sipoc-editor:rounded-xl @max-[68rem]/sipoc-editor:border @max-[68rem]/sipoc-editor:grid-cols-1",
        dragging && "opacity-50",
        dropTarget && "ring-2 ring-inset ring-pb-accent/50",
      )}
      ref={setNodeRef}
      style={{ transform: DndCSS.Transform.toString(transform), transition }}
      data-sipoc-row-id={row.id}
    >
      {/* Suppliers is the leading column — no inline-start divider. */}
      {listCell("suppliers", false)}
      {listCell("inputs")}
      <SipocProcessCell
        row={row}
        index={index}
        rowCount={rowCount}
        readOnly={readOnly}
        expandedLayout={expandedLayout}
        s={s}
        onProcessChange={onProcessChange}
        onAddRow={onAddRow}
        onRemoveRow={onRemoveRow}
        onMoveRow={onMoveRow}
        dragHandle={{ setActivatorNodeRef, attributes, listeners }}
        linkState={linkState}
      />
      {listCell("outputs")}
      {listCell("customers")}
    </div>
  );
}

function SipocListCell({
  column,
  entries,
  rowId,
  readOnly,
  expandedLayout,
  s,
  withDivider,
  onAdd,
  onUpdate,
  onRemove,
  onClassificationChange,
  onMove,
  onUpdateDetails,
}: {
  column: ListColumn;
  entries: SipocPartyEntry[];
  /** PB-4923 AC5 — threaded to each entry so it can name its own target. */
  rowId: string;
  readOnly: boolean | undefined;
  expandedLayout: boolean;
  s: SipocScale;
  withDivider?: boolean;
  onAdd: () => void;
  onUpdate: (entryId: string, text: string) => void;
  onRemove: (entryId: string) => void;
  onClassificationChange: (
    entryId: string,
    classification: SipocPartyClassification | undefined,
  ) => void;
  onMove: (entryId: string, nextIndex: number) => void;
  onUpdateDetails: (entryId: string, details: SipocEntryDetailValues) => void;
}): ReactElement {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col",
        s.cell,
        withDivider && "border-s border-pb-border",
        "@max-[68rem]/sipoc-editor:border-s-0 @max-[68rem]/sipoc-editor:border-t @max-[68rem]/sipoc-editor:border-pb-border",
      )}
    >
      {expandedLayout ? (
        <div className="hidden items-center gap-2 text-xs font-semibold text-pb-text-secondary @max-[68rem]/sipoc-editor:flex">
          <span
            className={cn("h-1.5 w-1.5 rounded-full", COLUMN_DOT[column])}
            aria-hidden="true"
          />
          {COLUMN_TITLES[column]}
        </div>
      ) : null}
      <SortableContext
        items={entries.map((entry) => entry.id)}
        strategy={verticalListSortingStrategy}
      >
        {entries.map((entry, index) => (
          <SipocSortableEntry
            key={entry.id}
            column={column}
            index={index}
            entry={entry}
            rowId={rowId}
            readOnly={readOnly}
            expandedLayout={expandedLayout}
            s={s}
            onChange={(text) => onUpdate(entry.id, text)}
            onRemove={() => onRemove(entry.id)}
            onClassificationChange={(classification) =>
              onClassificationChange(entry.id, classification)
            }
            onMove={(nextIndex) => onMove(entry.id, nextIndex)}
            onUpdateDetails={(details) => onUpdateDetails(entry.id, details)}
          />
        ))}
      </SortableContext>
      {readOnly ? null : (
        <Button
          variant="ghost"
          size="sm"
          onClick={onAdd}
          className={cn(
            "justify-start text-pb-text-muted hover:text-pb-iris-text",
            s.smallButton,
          )}
        >
          <Plus className={s.entryIcon} aria-hidden="true" />
          {ADD_LABELS[column]}
        </Button>
      )}
    </div>
  );
}

/** Animate the complete entry (including its details action), as the Home
 * dashboard animates its sortable card rather than only its drag grip. */
function SipocSortableEntry({
  column,
  index,
  entry,
  rowId,
  readOnly,
  expandedLayout,
  s,
  onChange,
  onRemove,
  onClassificationChange,
  onMove,
  onUpdateDetails,
}: {
  column: ListColumn;
  index: number;
  entry: SipocPartyEntry;
  rowId: string;
  readOnly: boolean | undefined;
  expandedLayout: boolean;
  s: SipocScale;
  onChange: (text: string) => void;
  onRemove: () => void;
  onClassificationChange: (
    classification: SipocPartyClassification | undefined,
  ) => void;
  onMove: (nextIndex: number) => void;
  onUpdateDetails: (details: SipocEntryDetailValues) => void;
}): ReactElement {
  const {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isOver,
    isDragging,
  } = useSortable({
    id: entry.id,
    disabled: readOnly === true,
    data: { kind: "entry", rowId, column, entryId: entry.id },
  });
  const style: CSSProperties = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };
  const input = (
    <SipocEntryInput
      column={column}
      index={index}
      entry={entry}
      rowId={rowId}
      readOnly={readOnly}
      s={s}
      onChange={onChange}
      onRemove={onRemove}
      onClassificationChange={onClassificationChange}
      onMove={onMove}
      dragHandle={{ setActivatorNodeRef, attributes, listeners }}
      isOver={isOver}
      isDragging={isDragging}
    />
  );
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="motion-reduce:!transition-none"
    >
      {column === "inputs" || column === "outputs" ? (
        <SipocEntryDetails
          entry={entry}
          label={`${COLUMN_TITLES[column]} entry ${index + 1}`}
          readOnly={readOnly}
          expanded={expandedLayout}
          iconClassName={s.entryIcon}
          onChange={onUpdateDetails}
        >
          {input}
        </SipocEntryDetails>
      ) : (
        input
      )}
    </div>
  );
}

function SipocEntryInput({
  column,
  index,
  entry,
  rowId,
  readOnly,
  s,
  onChange,
  onRemove,
  onClassificationChange,
  onMove,
  dragHandle: { setActivatorNodeRef, attributes, listeners },
  isOver,
  isDragging,
}: {
  column: ListColumn;
  index: number;
  entry: SipocPartyEntry;
  /** PB-4923 AC5 — the parent row, so this entry can name its own target. */
  rowId: string;
  readOnly: boolean | undefined;
  s: SipocScale;
  onChange: (text: string) => void;
  onRemove: () => void;
  onClassificationChange: (
    classification: SipocPartyClassification | undefined,
  ) => void;
  onMove: (nextIndex: number) => void;
  dragHandle: SortableHandle;
  isOver: boolean;
  isDragging: boolean;
}): ReactElement {
  // Entry text wraps like the design's chip instead of clipping: the
  // textarea grows with its content (see useAutoGrow).
  const textRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(textRef, entry.text);
  const lease = useFieldEditSession(
    widgetTargetKey([
      { kind: "item", collection: "rows", itemId: rowId },
      { kind: "item", collection: column, itemId: entry.id },
      { kind: "prop", name: "text" },
    ]),
  );
  if (readOnly) {
    return (
      <div
        className={cn(
          "whitespace-pre-wrap break-words rounded-lg border border-pb-border bg-pb-card-elevated/60 text-pb-text",
          s.entryReadOnly,
        )}
      >
        <div>
          {entry.text || (
            <span className="text-pb-text-muted italic">Empty</span>
          )}
        </div>
        {column === "suppliers" || column === "customers" ? (
          <div className={cn("text-pb-text-muted", s.entryMeta)}>
            {entry.classification === "internal"
              ? "Internal"
              : entry.classification === "external"
                ? "External"
                : "Type not set"}
          </div>
        ) : null}
      </div>
    );
  }
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === "Backspace" && entry.text === "") {
      e.preventDefault();
      onRemove();
    }
  };
  return (
    <div
      className={cn(
        // The box is the text field's frame (ADR-0092 d8): it takes the
        // field look while its textarea is focus-visible, and a focused grip
        // or button shows only its own ring.
        "group/entry flex items-center rounded-lg border border-pb-border bg-pb-card-elevated/60 transition-colors hover:border-pb-border-strong has-[textarea:focus-visible]:focus-frame",
        s.entry,
        isOver && "ring-2 ring-inset ring-pb-accent/50",
        isDragging && "opacity-50",
      )}
    >
      {/* The 20px handle is the 24px Button step (look row OK-01). */}
      <Button
        variant="ghost"
        size="xs"
        iconOnly
        ref={setActivatorNodeRef}
        {...attributes}
        aria-label={`Move ${COLUMN_TITLES[column]} entry ${index + 1}`}
        onPointerDown={(event) => {
          event.stopPropagation();
          listeners?.onPointerDown?.(event);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp") {
            event.preventDefault();
            onMove(index - 1);
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            onMove(index + 1);
          }
        }}
        className="nodrag nopan touch-none shrink-0 cursor-grab"
      >
        <GripVertical className={s.entryIcon} aria-hidden="true" />
      </Button>
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a content-sized entry field inside its bordered entry box; Textarea ghost adds a hover wash and an edge, and no approved look row lists the change. */}
      <textarea
        ref={textRef}
        data-sipoc-focus-id={entry.id}
        aria-label={`${COLUMN_TITLES[column]} entry ${index + 1}`}
        value={entry.text}
        rows={1}
        maxLength={SIPOC_TEXT_MAX}
        onChange={(e) => onChange(e.target.value)}
        onFocus={lease.onFocus}
        onBlur={lease.onBlur}
        onKeyDown={handleKeyDown}
        placeholder="…"
        className={cn(
          "field-sizing-content min-w-0 flex-1 resize-none overflow-hidden bg-transparent text-pb-text placeholder:text-pb-text-muted/70 outline-none",
          s.entryText,
        )}
      />
      {column === "suppliers" || column === "customers" ? (
        // Cycling classify pill (Int → Ext → not set), as the design draws it.
        // eslint-disable-next-line ds/no-hand-built -- kept: a cycling Int/Ext classification pill with a 4px corner in the entry row; no DS pill toggles through three values, and no approved look row lists the change.
        <button
          type="button"
          aria-label={`${COLUMN_TITLES[column]} classification ${index + 1}: ${
            entry.classification === "internal"
              ? "Internal"
              : entry.classification === "external"
                ? "External"
                : "Not set"
          }. Change`}
          onClick={() =>
            onClassificationChange(
              entry.classification === "internal"
                ? "external"
                : entry.classification === "external"
                  ? undefined
                  : "internal",
            )
          }
          className={cn(
            "shrink-0 rounded border font-bold uppercase tracking-wide transition-colors focus-ring",
            s.classify,
            entry.classification === "external"
              ? "border-pb-amber/35 bg-pb-amber/15 text-pb-amber-text"
              : entry.classification === "internal"
                ? "border-pb-cyan/30 bg-pb-cyan/15 text-pb-cyan-text"
                : "border-pb-border text-pb-text-muted",
          )}
        >
          {entry.classification === "external"
            ? "Ext"
            : entry.classification === "internal"
              ? "Int"
              : "—"}
        </button>
      ) : null}
      {/* eslint-disable-next-line ds/no-hand-built -- kept: a hover-revealed 28px remove control with a red hover ink; Button ghost critical paints a red wash, and no approved look row lists the change. */}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove entry"
        className={cn(
          "flex w-7 shrink-0 items-center justify-center rounded text-pb-text-muted opacity-0 transition-opacity hover:pb-overlay-paint hover:text-pb-red-text focus-visible:opacity-100 group-hover/entry:opacity-60 focus-ring",
          s.entryControl,
        )}
      >
        <Trash2 className={s.entryIcon} aria-hidden="true" />
      </button>
    </div>
  );
}

function SipocProcessCell({
  row,
  index,
  rowCount,
  readOnly,
  expandedLayout,
  s,
  onProcessChange,
  onAddRow,
  onRemoveRow,
  onMoveRow,
  dragHandle: { setActivatorNodeRef, attributes, listeners },
  linkState,
}: {
  row: SipocRow;
  index: number;
  rowCount: number;
  readOnly: boolean | undefined;
  expandedLayout: boolean;
  s: SipocScale;
  onProcessChange: (text: string) => void;
  onAddRow: () => void;
  onRemoveRow: () => void;
  onMoveRow: (nextIndex: number) => void;
  dragHandle: SortableHandle;
  /** Absent when the host passes no link map: no map line is drawn. */
  linkState: SipocLinkState | undefined;
}): ReactElement {
  const boundary =
    index === 0 ? "start" : index === rowCount - 1 ? "end" : null;
  // The step name wraps like the design's box instead of clipping: the
  // textarea grows with its content (see useAutoGrow).
  const nameRef = useRef<HTMLTextAreaElement>(null);
  useAutoGrow(nameRef, row.process);
  const processLease = useFieldEditSession(
    widgetTargetKey([
      { kind: "item", collection: "rows", itemId: row.id },
      { kind: "prop", name: "process" },
    ]),
  );
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col items-stretch justify-center border-x border-pb-border bg-pb-tint-7/10 @max-[68rem]/sipoc-editor:order-first @max-[68rem]/sipoc-editor:border-x-0",
        s.processCell,
      )}
    >
      {expandedLayout ? (
        <div className="hidden items-center gap-2 text-xs font-semibold text-pb-iris-text @max-[68rem]/sipoc-editor:flex">
          <span
            className="h-1.5 w-1.5 rounded-full bg-pb-tint-7"
            aria-hidden="true"
          />
          Process
        </div>
      ) : null}
      <div
        className={cn(
          // The box is the step name's frame, as the entry box is.
          "relative flex items-center rounded-lg border border-pb-accent/30 bg-pb-purple-soft shadow-sm has-[textarea:focus-visible]:focus-frame",
          s.processBox,
        )}
      >
        {readOnly ? null : (
          // The 16px step handle is the 24px Button step (look row OK-01).
          <Button
            variant="ghost"
            size="xs"
            iconOnly
            ref={setActivatorNodeRef}
            {...attributes}
            aria-label={`Reorder step ${index + 1}`}
            onPointerDown={(event) => {
              event.stopPropagation();
              listeners?.onPointerDown?.(event);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowUp") {
                event.preventDefault();
                onMoveRow(index - 1);
              }
              if (event.key === "ArrowDown") {
                event.preventDefault();
                onMoveRow(index + 1);
              }
            }}
            className={cn(
              "nodrag nopan touch-none shrink-0 cursor-grab",
              s.grip,
            )}
          >
            <GripVertical className={s.gripIcon} aria-hidden="true" />
          </Button>
        )}
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full bg-pb-tint-7/25 font-bold tabular-nums text-pb-text",
            s.badge,
          )}
        >
          {index + 1}
        </span>
        {readOnly ? (
          <div
            className={cn(
              "min-w-0 flex-1 whitespace-pre-wrap break-words font-semibold text-pb-text",
              s.processText,
            )}
          >
            {row.process || (
              <span className="font-normal italic text-pb-text-muted">
                Process step
              </span>
            )}
          </div>
        ) : (
          // eslint-disable-next-line ds/no-hand-built -- kept: a content-sized step name inside the process box; Textarea ghost adds a hover wash and an edge, and no approved look row lists the change.
          <textarea
            ref={nameRef}
            data-sipoc-focus-id={row.id}
            aria-label="Process step"
            value={row.process}
            rows={1}
            maxLength={SIPOC_TEXT_MAX}
            onChange={(e) => onProcessChange(e.target.value)}
            onFocus={processLease.onFocus}
            onBlur={processLease.onBlur}
            // Enter names the step and moves on to the next one, like a list;
            // Shift+Enter keeps a line break. An Enter that confirms an IME
            // composition is the input method's, never ours; Safari reports
            // that Enter with `isComposing` false but `keyCode` 229.
            onKeyDown={(event) => {
              if (
                event.key !== "Enter" ||
                event.shiftKey ||
                event.nativeEvent.isComposing ||
                event.keyCode === 229
              ) {
                return;
              }
              event.preventDefault();
              onAddRow();
            }}
            placeholder="Process step"
            className={cn(
              "field-sizing-content min-w-0 flex-1 resize-none overflow-hidden bg-transparent font-semibold text-pb-text placeholder:text-pb-text-muted/70 outline-none",
              s.processText,
            )}
          />
        )}
        {boundary ? (
          <span
            className={cn(
              "absolute rounded-full border font-bold uppercase tracking-wide",
              s.boundary,
              boundary === "start"
                ? "border-pb-cyan/40 bg-pb-cyan/15 text-pb-cyan-text"
                : "border-pb-green/40 bg-pb-green/15 text-pb-green-text",
            )}
          >
            {boundary === "start" ? "Start" : "End"}
          </span>
        ) : null}
      </div>
      {linkState === undefined ? null : (
        <div
          className={cn(
            "flex items-center justify-center gap-1 whitespace-normal text-center",
            s.link,
            linkState === "added"
              ? "text-pb-green-text"
              : linkState === "different"
                ? "text-pb-amber-text"
                : linkState === "map-item-removed"
                  ? "text-pb-red-text"
                  : "text-pb-text-muted",
          )}
        >
          <span
            className={cn(
              "shrink-0 rounded-full",
              s.linkDot,
              linkState === "added"
                ? "bg-pb-green"
                : linkState === "different"
                  ? "bg-pb-amber"
                  : linkState === "map-item-removed"
                    ? "bg-pb-red"
                    : "bg-pb-text-muted",
            )}
            aria-hidden="true"
          />
          {linkState === "added"
            ? "On Current State map"
            : linkState === "different"
              ? "Different from map"
              : linkState === "map-item-removed"
                ? "Map item removed"
                : "Not on Current State map"}
        </div>
      )}
      {readOnly ? null : (
        // eslint-disable-next-line ds/no-hand-built -- kept: a hover-revealed text action with a 4px corner and red hover ink; no Button variant paints it, and no approved look row lists the change.
        <button
          type="button"
          onClick={onRemoveRow}
          aria-label="Remove step"
          className={cn(
            "flex items-center gap-1 self-center rounded text-pb-text-secondary opacity-0 transition-opacity hover:pb-overlay-paint hover:text-pb-red-text focus-visible:opacity-100 focus-ring group-hover/row:opacity-100",
            s.removeStep,
          )}
        >
          <Trash2 className={s.entryIcon} aria-hidden="true" />
          Remove step
        </button>
      )}
    </div>
  );
}
